package com.evoapps.evoshift.domain.model

/**
 * The kinds of notification a person can switch push off for (SH-73). The in-app inbox keeps
 * everything; only the push to their devices follows these switches. The server decides with the
 * same mapping (functions/notificationPrefs.js): keep both in step.
 */
enum class NotificationCategory {
    /** A roster published, a shift assigned, a note on one's shift. */
    ROSTER,
    /** Shift swaps and the marketplace: requests, claims, decisions, open shifts. */
    SWAPS,
    /** Absence requests (managers). */
    ABSENCES,
    /** Requests to fill in availability. */
    AVAILABILITY,
    /** Someone has not clocked in (managers only). */
    NO_SHOWS,
    /** Posts of the managers to the team (SH-118). */
    ANNOUNCEMENTS,
    /** A closing checklist is not done after the shift (the person on it and the managers, SH-121). */
    CHECKLISTS,
    /** Someone applied to a job post (managers only, SH-125). */
    HIRING;

    /** Only managers (and owners) get these. */
    val managersOnly: Boolean get() = this == NO_SHOWS || this == ABSENCES || this == HIRING

    companion object {
        /** The category of [notification]; null for those always pushed. */
        fun of(notification: Notification): NotificationCategory? = when (notification.type) {
            NotificationType.ROSTER_PUBLISHED, NotificationType.SHIFT_ASSIGNED -> ROSTER
            NotificationType.SHIFT_SWAP_REQUEST, NotificationType.SHIFT_SWAP_APPROVED, NotificationType.SHIFT_SWAP_REJECTED,
            NotificationType.SHIFT_CLAIM_PENDING, NotificationType.SHIFT_CLAIM_APPROVED, NotificationType.SHIFT_CLAIM_REJECTED,
            NotificationType.OPEN_SHIFTS_AVAILABLE -> SWAPS
            NotificationType.AVAILABILITY_REQUEST -> AVAILABILITY
            NotificationType.SHIFT_NO_SHOW -> NO_SHOWS
            NotificationType.CHECKLIST_OVERDUE -> CHECKLISTS
            NotificationType.GENERAL -> when (notification.data[NotificationData.KIND]) {
                NotificationData.Kind.ABSENCE_REQUEST -> ABSENCES
                NotificationData.Kind.NOW_PUBLIC, NotificationData.Kind.MARKETPLACE -> SWAPS
                NotificationData.Kind.ANNOUNCEMENT -> ANNOUNCEMENTS
                NotificationData.Kind.SHIFT_NOTE -> ROSTER
                NotificationData.Kind.NEW_APPLICANT -> HIRING
                else -> null
            }
        }
    }
}

/**
 * A person's push switches (users/{uid}/settings/notifications). Everything is on until switched
 * off, also categories added later. [trialEmails] (SH-241): the short tips owners get by e-mail
 * during the trial (functions/trialEmails.js); their unsubscribe link switches it off too.
 */
data class NotificationSettings(
    val pushOff: Set<NotificationCategory> = emptySet(),
    val trialEmails: Boolean = true
) {
    fun isOn(category: NotificationCategory): Boolean = category !in pushOff

    fun with(category: NotificationCategory, on: Boolean): NotificationSettings =
        copy(pushOff = if (on) pushOff - category else pushOff + category)

    /** Whether [notification] is pushed to the person's devices. */
    fun pushes(notification: Notification): Boolean = NotificationCategory.of(notification)?.let(::isOn) ?: true
}
